import api from '@/http/api';
import { useSocketStore } from '@/socketStore';
import { useAuthStore } from '@/store';
import type { MessageType, OrderType } from '@/types';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@univdiam/ui/components/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@univdiam/ui/components/dialog';
import { ChatSection } from '@univdiam/ui/customComponents/OrderDetail/ChatSection';
import { formatTimeInUserTimezone } from '@univdiam/ui/lib/formatedDates';
import { renderRichText, richTextClass } from '@univdiam/ui/lib/renderRichText';
import { cn } from '@univdiam/ui/lib/utils';
import { useOrderChat } from '@univdiam/ui/hooks/useOrderChat';

import { format } from 'date-fns';
import { Square, SquareCheck } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';

// Keyset paging: `cursor` is the oldest message id already loaded, so the
// window is anchored to a row rather than to an offset from the newest
// message. An offset shifts every time a message arrives mid-scroll, which
// silently skipped rows and served others twice.
const getChats = (order_id: string | number, cursor: number | null) =>
    api.get(`/chat/order/${order_id}?limit=20${cursor ? `&cursor=${cursor}` : ''}`);

// Everything newer than a message we already hold. Used only after a reconnect.
const getChatsAfter = (order_id: string | number, afterId: number) =>
    api.get(`/chat/order/${order_id}?limit=50&after=${afterId}`);

const Chat = ({ orderDetailData, onBack, headerActions }: { orderDetailData: Partial<OrderType>, onBack?: () => void, headerActions?: React.ReactNode }) => {
    const { user, setNotificationCount } = useAuthStore();
    const getMessage = useSocketStore((s) => s.listenForMessages);
    const sendMessage = useSocketStore((s) => s.sendMessage);
    const setActiveOrderChat = useSocketStore((s) => s.setActiveOrderChat);
    const reconnectNonce = useSocketStore((s) => s.reconnectNonce);
    const isConnected = useSocketStore((s) => s.isConnected);
    const sendNotification = useSocketStore((s) => s.sendNotification);
    const recieveNotification = useSocketStore((s) => s.recieveNotification);
    const queryClient = useQueryClient()

    // All chat delivery machinery lives in the shared hook: identity,
    // reconciliation, pagination, acknowledgements and reconnect recovery. Both
    // portals use the same one, so a fix here cannot land in only one of them.
    const {
        messages,
        handleSend,
        handleRetry,
        isOffline,
        chatQueryKey,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isLoading,
    } = useOrderChat({
        orderDbId: orderDetailData.id,
        orderPublicId: orderDetailData.order_id,
        fetchPage: getChats,
        fetchAfter: getChatsAfter,
        listenForMessages: getMessage,
        sendMessage,
        setActiveOrderChat,
        recieveNotification,
        viewerRole: user?.role,
        reconnectNonce,
        isConnected,
    });

    // Which order this component has already marked read. A boolean latch here
    // was never reset, so opening a second chat without unmounting -- exactly
    // what AllChat does, since <Chat> has no key -- skipped the request and the
    // unread badge came back on the next refresh. Keyed by order id it re-fires
    // on every switch, while still collapsing StrictMode's double mount.
    const markedOrderId = useRef<string | null>(null);

    // Where this reader had got to before opening. Drives the "N new messages"
    // divider; see ChatSection's unreadFromId. null until the response lands --
    // 0 is a real watermark meaning "has read nothing here", so the unknown
    // state cannot share it or the divider flashes above the whole history.
    const [unreadFromId, setUnreadFromId] = useState<number | null>(null);

    // Mark messages as read when the chat is opened (Fix #11)
    useEffect(() => {
        if (!orderDetailData.id || markedOrderId.current === orderDetailData.id) return;
        markedOrderId.current = orderDetailData.id;
        setUnreadFromId(null);
        api.post(`/order/update-notif-chat`, {
            order_id: orderDetailData.id,
            receiver_id: user?.id,
            receiver_type: user?.role
        }).then((res) => {
            // Captured server-side before the mark, so it is the reader's
            // position on entry. Set once per order: later live messages must
            // not move the divider.
            // || 0 collapses both a real 0 and a missing field (NaN) to 0,
            // which is the same safe reading: "has read nothing here".
            if (res.data.success) setUnreadFromId(Number(res.data.previous_watermark) || 0);
            // The reader's own badge: the chat_read broadcast goes to the OTHER
            // side only, so nothing else refreshes this key for the actor.
            if (res.data.success) queryClient.invalidateQueries({ queryKey: ['sidebar-chat-unread'] });
            if (res.data.success && res.data.unseenCount !== undefined) {
                setNotificationCount(res.data.unseenCount);
                sendNotification({
                    action_by: user?.role!,
                    action_by_id: user?.id.toString()!,
                    action_type: 'chat_read',
                    order_id: orderDetailData.id!,
                    title: `Read Receipt`,
                    metadata: {
                        order_ref: orderDetailData.id!,
                        order_id: orderDetailData.order_id!,
                    },
                });
            }
        }).catch(console.error);
    }, [orderDetailData.id]);

    const [isMediaUploading, setIsMediaUploading] = useState(false);
    const handleSendToUpload = async (files: File[]) => {
        setIsMediaUploading(true);
        try {
            const formData = new FormData();
            files.forEach((file) => formData.append('files', file));

            const response = await api.post(
                `/upload?customerId=${user?.id}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            return response.data.files ?? [];
        } catch (error) {
            toast.error(`Error occurred while uploading media: ${error}`);
            return [];
        } finally {
            setIsMediaUploading(false);
        }
    };

    // Attention modal logic
    const [openAttentionModal, setAttentionModal] = useState(false);
    const [attentionMessages, setAttentionMessages] = useState<MessageType[]>([]);
    const [changedIndexes, setChangedIndexes] = useState<number[]>([]);

    useEffect(() => {
        if (messages && openAttentionModal) {
            // clone only attention-marked messages (copy, not filter updates away)
            const filtered = messages
                .filter((m) => Number(m.isAttention) === 1)
                .map((m) => ({ ...m }));
            console.log('filtered', filtered)
            setAttentionMessages(filtered);
            setChangedIndexes([]);
        }
    }, [messages, openAttentionModal]);

    const handleToggle = (index: number) => {
        setAttentionMessages((prev) =>
            prev.map((m, i) => {
                if (i === index) {
                    const newIsAttention = m.isAttention ? 0 : 1;
                    return { ...m, isAttention: newIsAttention };
                }
                return m;
            })
        );

        setChangedIndexes((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
    };

    const handleSaveChanges = async () => {
        try {
            const uncheckedIds = attentionMessages
                .filter((m) => m.isAttention === 0)
                .map((m) => m.id);

            if (uncheckedIds.length === 0) {
                setAttentionModal(false);
                return;
            }

            await api.post(`/chat/order/markAttention`, {
                ids: uncheckedIds,
            }).then(() => {
                queryClient.invalidateQueries({ queryKey: chatQueryKey })
            }).catch(() => {
                toast.error('Something went wrong!')
            });

            setAttentionModal(false);
        } catch (error) {
            console.error("Failed to update attention flags:", error);
            // Optional: show a toast or alert
            // toast.error("Something went wrong while saving changes");
        }
    };

    return (
        <div className='h-full'>
            <ChatSection
                onBack={onBack}
                messages={messages}
                onSendMessage={handleSend}
                onRetryMessage={handleRetry}
                isOffline={isOffline}
                orderId={orderDetailData.order_id}
                user={user}
                orderRef={orderDetailData.id!}
                handleSendToUpload={handleSendToUpload}
                isMediaUploading={isMediaUploading}
                isAdmin={true}
                handleOpenAttentionModal={() => setAttentionModal(true)}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                isLoading={isLoading}
                unreadFromId={unreadFromId}
                headerActions={headerActions}
            />

            <Dialog open={openAttentionModal} onOpenChange={setAttentionModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="text-xl">
                            Attention Required Texts
                        </DialogTitle>
                        <DialogDescription className="mt-6 flex flex-col gap-4 overflow-y-auto max-h-[60vh] pr-2">
                            {attentionMessages.length > 0 ? (
                                attentionMessages.map((m, index) => (
                                    <div
                                        key={m.id || index}
                                        className="border px-3 py-4 flex justify-between items-start rounded-md gap-3 overflow-hidden shrink-0"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div
                                                className={cn("font-semibold text-lg break-all", richTextClass)}
                                                dangerouslySetInnerHTML={{ __html: renderRichText(m.message) }}
                                            />
                                            <p className="caption text-[#777777] !text-xs">
                                                {format(new Date(m.created_at), 'dd MMM yyyy')} •{' '}
                                                {formatTimeInUserTimezone(m.created_at)}
                                            </p>
                                        </div>

                                        <div className="shrink-0 flex items-start min-w-[20px]">
                                            {m.isAttention ? (
                                                <SquareCheck
                                                    onClick={() => handleToggle(index)}
                                                    className="w-5 h-5 cursor-pointer shrink-0"
                                                />
                                            ) : (
                                                <Square
                                                    onClick={() => handleToggle(index)}
                                                    className="w-5 h-5 cursor-pointer shrink-0"
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-gray-500 text-center py-4">
                                    No attention messages found.
                                </p>
                            )}

                            {changedIndexes.length > 0 && (
                                <Button
                                    onClick={handleSaveChanges}
                                >
                                    Save Changes
                                </Button>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Chat;
